import { IonIcon } from '@ionic/react';
import { alertCircle, checkmarkCircle, timeOutline } from 'ionicons/icons';
import React, { useMemo } from 'react';
import { formatStepDuration } from '../../automation/runProgress';
import type { AutomationExecutionEntry } from '../../types/AutomationWorkflow.types';

const startOfDay = (value: Date): number =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();

const dayLabel = (timestamp: number): string => {
  const today = startOfDay(new Date());
  const oneDay = 24 * 60 * 60 * 1000;
  if (timestamp === today) return 'Today';
  if (timestamp === today - oneDay) return 'Yesterday';
  return new Date(timestamp).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
};

const entryDuration = (entry: AutomationExecutionEntry): number | null => {
  if (!entry.finishedAt) return null;
  const started = new Date(entry.startedAt).getTime();
  const finished = new Date(entry.finishedAt).getTime();
  if (Number.isNaN(started) || Number.isNaN(finished)) return null;
  const elapsed = finished - started;
  return elapsed >= 0 ? elapsed : null;
};

const fileName = (filePath?: string | null): string => {
  if (!filePath) return '';
  return filePath.split(/[/\\]/).pop() ?? '';
};

export const AutomationRunHistory: React.FC<{
  executionLog: AutomationExecutionEntry[];
}> = ({ executionLog }) => {
  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

    let thisMonth = 0;
    let succeeded = 0;
    let settled = 0;
    let durationTotal = 0;
    let durationCount = 0;

    executionLog.forEach((entry) => {
      const started = new Date(entry.startedAt).getTime();
      if (!Number.isNaN(started) && started >= monthStart) thisMonth += 1;
      if (entry.status === 'success' || entry.status === 'error') {
        settled += 1;
        if (entry.status === 'success') succeeded += 1;
      }
      const duration = entryDuration(entry);
      if (duration != null) {
        durationTotal += duration;
        durationCount += 1;
      }
    });

    return {
      thisMonth,
      successRate: settled ? Math.round((succeeded / settled) * 100) : null,
      averageDuration: durationCount ? durationTotal / durationCount : null,
    };
  }, [executionLog]);

  const groups = useMemo(() => {
    const buckets = new Map<number, AutomationExecutionEntry[]>();
    executionLog.forEach((entry) => {
      const started = new Date(entry.startedAt);
      if (Number.isNaN(started.getTime())) return;
      const key = startOfDay(started);
      buckets.set(key, [...(buckets.get(key) ?? []), entry]);
    });
    return [...buckets.entries()].sort((a, b) => b[0] - a[0]);
  }, [executionLog]);

  if (executionLog.length === 0) {
    return (
      <section className="automation-run-history">
        <p className="automation-empty-panel">
          Runs will show up here after you save and execute this workflow.
        </p>
      </section>
    );
  }

  return (
    <section className="automation-run-history">
      <div className="automation-history-stats">
        <HistoryStat label="Runs this month" value={String(stats.thisMonth)} />
        <HistoryStat
          label="Success rate"
          value={stats.successRate == null ? '—' : `${stats.successRate}%`}
        />
        <HistoryStat
          label="Average run"
          value={
            stats.averageDuration == null
              ? '—'
              : formatStepDuration(stats.averageDuration)
          }
        />
      </div>

      {groups.map(([day, entries]) => (
        <div key={day} className="automation-history-group">
          <p className="automation-history-day">{dayLabel(day)}</p>
          <ul className="automation-history-rows">
            {entries.map((entry) => (
              <HistoryRow key={entry.id} entry={entry} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
};

const HistoryRow: React.FC<{ entry: AutomationExecutionEntry }> = ({
  entry,
}) => {
  const duration = entryDuration(entry);
  const file = fileName(entry.filePath);

  return (
    <li className={`automation-history-row status-${entry.status}`}>
      <span className="automation-history-time">
        {new Date(entry.startedAt).toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        })}
      </span>
      <IonIcon
        className="automation-history-icon"
        icon={
          entry.status === 'success'
            ? checkmarkCircle
            : entry.status === 'error'
              ? alertCircle
              : timeOutline
        }
        aria-hidden
      />
      <span className="automation-history-message">
        {entry.message ?? entry.status}
      </span>
      {file && <span className="automation-history-file">{file}</span>}
      {duration != null && (
        <span className="automation-history-duration">
          {formatStepDuration(duration)}
        </span>
      )}
    </li>
  );
};

const HistoryStat: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="automation-history-stat">
    <span className="automation-history-stat-value">{value}</span>
    <span className="automation-history-stat-label">{label}</span>
  </div>
);
